import { lawnCareData } from './lawnCareData';
import { 
  getZoneTaskRecommendation, 
  getZoneMonthlyAdjustments,
  getZoneCriticalAlerts,
  zoneGrassTypePriorities 
} from './zoneCustomization';
import { 
  getStateExtensionService,
  getStateModifications,
  isTaskInappropriateForState 
} from './stateCustomization';

// Get zone-customized lawn care data with state-specific modifications
export const getZoneCustomizedData = (zone, state = null) => {
  const customizedData = {};
  
  Object.keys(lawnCareData).forEach(month => {
    const monthData = lawnCareData[month];
    const monthAdjustments = getZoneMonthlyAdjustments(month, zone);
    
    customizedData[month] = {
      ...monthData,
      zoneAdjustments: monthAdjustments.adjustments,
      sections: monthData.sections.map(section => ({
        ...section,
        tasks: enhanceTasksWithZoneInfo(section.tasks, zone, month, state)
      }))
    };
  });

  return customizedData;
};

// Enhance tasks with zone-specific information and state customizations
const enhanceTasksWithZoneInfo = (tasks, zone, month, state = null) => {
  return tasks.map(task => {
    // Skip tasks inappropriate for the state
    if (state && isTaskInappropriateForState(task.text, state)) {
      return {
        ...task,
        priority: 'low',
        zoneNote: 'This task may not apply to your climate zone.',
        stateFiltered: true
      };
    }
    
    const zoneRecommendation = getZoneTaskRecommendation(task.text, zone);
    const grassTypes = zoneGrassTypePriorities[zone];
    
    let enhancedTask = { ...task };
    
    // Add zone-specific timing and notes
    if (zoneRecommendation) {
      enhancedTask.zoneTiming = zoneRecommendation.timing;
      enhancedTask.zoneNote = zoneRecommendation.note;
      
      // Update priority for critical zone-specific tasks
      if (zoneRecommendation.timing && month === getCurrentMonthForTiming(zoneRecommendation.timing)) {
        enhancedTask.priority = 'critical';
        enhancedTask.zoneAlert = true;
      }
    }
    
    // Add grass type relevance
    if (grassTypes && task.text.toLowerCase().includes('grass')) {
      enhancedTask.recommendedGrasses = grassTypes.slice(0, 3);
    }
    
    // Apply zone and state-specific task modifications
    enhancedTask = applyZoneSpecificModifications(enhancedTask, zone, month, state);
    
    return enhancedTask;
  });
};

// Apply zone-specific task modifications with state customizations
const applyZoneSpecificModifications = (task, zone, month, state = null) => {
  const taskText = task.text.toLowerCase();
  let modifiedTask = { ...task };
  
  // Apply state-specific extension service replacements
  if (state && modifiedTask.text.toLowerCase().includes('umass extension')) {
    const stateExtension = getStateExtensionService(state);
    modifiedTask.text = modifiedTask.text.replace(/umass extension/gi, stateExtension);
    // Replace the full extension laboratory name while preserving the store mention
    modifiedTask.text = modifiedTask.text.replace(
      /Send samples to ([^(]+)(\(or visit our Store for soil test kits\))/gi,
      `Send samples to ${stateExtension} $2`
    );
  }
  
  // Apply state-specific soil testing notes
  if (state && taskText.includes('soil test')) {
    const stateModifications = getStateModifications(state);
    if (stateModifications.soilTestingNote) {
      modifiedTask.zoneNote = `${modifiedTask.zoneNote || ''} ${stateModifications.soilTestingNote}`.trim();
    }
  }
  
  // Cool-season vs warm-season adjustments
  const isNorthernZone = ['3a', '3b', '4a', '4b', '5a', '5b'].includes(zone);
  const isSouthernZone = ['8a', '8b', '9a', '9b', '10a', '10b'].includes(zone);
  
  // Modify cool-season specific tasks for southern zones
  if (isSouthernZone && (taskText.includes('kentucky bluegrass') || taskText.includes('perennial ryegrass'))) {
    modifiedTask.zoneNote = `${task.zoneNote || ''} Consider warm-season alternatives like Bermuda or Zoysia in your climate.`.trim();
    modifiedTask.priority = 'low'; // Reduce priority for inappropriate grass types
  }
  
  // Modify warm-season specific tasks for northern zones
  if (isNorthernZone && (taskText.includes('bermuda') || taskText.includes('zoysia') || taskText.includes('st. augustine'))) {
    modifiedTask.zoneNote = `${task.zoneNote || ''} This warm-season grass may not be suitable for your climate. Consider cool-season alternatives.`.trim();
    modifiedTask.priority = 'low';
  }
  
  // Timing adjustments for specific tasks
  if (taskText.includes('pre-emergent')) {
    const zoneRec = getZoneTaskRecommendation(task.text, zone);
    if (zoneRec) {
      modifiedTask.text = `${task.text} (Zone ${zone}: ${zoneRec.timing})`;
    }
  }
  
  if (taskText.includes('overseed')) {
    const zoneRec = getZoneTaskRecommendation(task.text, zone);
    if (zoneRec && !zoneRec.timing.includes('Not')) {
      modifiedTask.text = `${task.text} (Zone ${zone}: ${zoneRec.timing})`;
    } else if (zoneRec && zoneRec.timing.includes('Not')) {
      modifiedTask.text = `${task.text} (Not recommended in your zone - consider warm-season alternatives)`;
      modifiedTask.priority = 'low';
      modifiedTask.zoneNote = zoneRec.note;
    }
  }
  
  // Remove state-specific references for other zones
  if (zone !== '5b' && zone !== '5a') {
    // Replace Massachusetts-specific timing with generic or zone-appropriate timing
    if (taskText.includes('massachusetts') || modifiedTask.text.toLowerCase().includes('massachusetts')) {
      modifiedTask.text = modifiedTask.text.replace(/\s*\(.*massachusetts.*\)/gi, '');
      modifiedTask.text = modifiedTask.text.replace(/for massachusetts/gi, 'for your zone');
      modifiedTask.text = modifiedTask.text.replace(/massachusetts/gi, 'your area');
      modifiedTask.text = modifiedTask.text.replace(/mid-june to july for your area/gi, 'optimal timing varies by zone');
    }
    
    // State-specific extension service replacement (handled above for all states)
    // This section now handled by state-specific logic above
    
    // Replace specific Massachusetts timing patterns
    if (modifiedTask.text.includes('Best timing:') && modifiedTask.text.includes('for your area')) {
      // Get zone-specific timing if available
      const zoneRec = getZoneTaskRecommendation(task.text, zone);
      if (zoneRec && zoneRec.timing) {
        modifiedTask.text = modifiedTask.text.replace(/Best timing:.*for your area/gi, `Best timing: ${zoneRec.timing}`);
      } else {
        modifiedTask.text = modifiedTask.text.replace(/Best timing:.*for your area/gi, 'Timing varies by zone - see zone recommendations');
      }
    }
  }
  
  // Snow and winter task adjustments
  if (isSouthernZone && (taskText.includes('snow') || taskText.includes('ice') || taskText.includes('frozen'))) {
    modifiedTask.priority = 'low';
    modifiedTask.zoneNote = 'This winter task may not apply to your warmer climate zone.';
  }
  
  return modifiedTask;
};

// Helper function to determine current month for timing
const getCurrentMonthForTiming = (timing) => {
  const timingLower = timing.toLowerCase();
  
  if (timingLower.includes('january')) return 'January';
  if (timingLower.includes('february')) return 'February';
  if (timingLower.includes('march')) return 'March';
  if (timingLower.includes('april')) return 'April';
  if (timingLower.includes('may')) return 'May';
  if (timingLower.includes('june')) return 'June';
  if (timingLower.includes('july')) return 'July';
  if (timingLower.includes('august')) return 'August';
  if (timingLower.includes('september')) return 'September';
  if (timingLower.includes('october')) return 'October';
  if (timingLower.includes('november')) return 'November';
  if (timingLower.includes('december')) return 'December';
  
  return null;
};

// Get zone-specific critical alerts for current month
export const getZoneAlertsForMonth = (zone, month) => {
  return getZoneCriticalAlerts(zone, month);
};

// Get recommended grass types for zone
export const getZoneGrassTypes = (zone) => {
  return zoneGrassTypePriorities[zone] || [];
};

// Check if a task is relevant for the user's zone
export const isTaskRelevantForZone = (task, zone) => {
  const taskText = task.text.toLowerCase();
  const grassTypes = zoneGrassTypePriorities[zone];
  
  if (!grassTypes) return true; // Default to relevant if no zone data
  
  // Check if task mentions grass types that aren't suitable for the zone
  const isNorthernZone = ['3a', '3b', '4a', '4b', '5a', '5b'].includes(zone);
  const isSouthernZone = ['8a', '8b', '9a', '9b', '10a', '10b'].includes(zone);
  
  // Cool-season grass tasks in warm zones
  if (isSouthernZone && (
    taskText.includes('kentucky bluegrass') || 
    taskText.includes('perennial ryegrass') ||
    taskText.includes('fine fescue')
  )) {
    return false;
  }
  
  // Warm-season grass tasks in cool zones  
  if (isNorthernZone && (
    taskText.includes('bermuda') ||
    taskText.includes('st. augustine') ||
    taskText.includes('bahia') ||
    taskText.includes('centipede')
  )) {
    return false;
  }
  
  return true;
};

// Get the current month for initial load
export const getCurrentMonth = () => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[new Date().getMonth()];
};